import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  ComposedChart, Area, Line, Legend,
  RadialBarChart, RadialBar, PolarAngleAxis,
} from 'recharts';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import { formatCurrency, compactCurrencyTick } from '../lib/calculations';
import { HowWorkedOut } from './howWorkedOut/HowWorkedOut';
import { RETENTION_FIGURES } from '../hooks/useRetentionStats.metrics';
import type {
  ProjectRetentionSummary, MonthlyRetentionTrend,
  RetentionAgingBucket, ProjectMonthlyRetention,
} from '../hooks/useRetentionStats';

/* ═══════════════════════════════════════════════════════════════════════════
   Animation Styles
   ═══════════════════════════════════════════════════════════════════════════ */

const retentionAnimStyles = `
@keyframes retFadeInUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}
.ret-chart-card {
  animation: retFadeInUp 0.5s ease-out both;
}
.ret-chart-card:nth-child(2) { animation-delay: 0.08s; }
.ret-chart-card:nth-child(3) { animation-delay: 0.16s; }
.ret-chart-card:nth-child(4) { animation-delay: 0.24s; }
`;

export function RetentionChartAnimationStyles() {
  return <style>{retentionAnimStyles}</style>;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Shared Styles
   ═══════════════════════════════════════════════════════════════════════════ */

const cardStyle: React.CSSProperties = {
  background: 'var(--bg-secondary, #fff)',
  borderRadius: 16, padding: '1.5rem',
  border: '1px solid var(--border-color, #e5e7eb)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};

const hdrStyle: React.CSSProperties = {
  fontSize: '0.95rem', fontWeight: 600,
  color: 'var(--text-primary, #111827)',
  marginBottom: '1rem', display: 'flex',
  alignItems: 'center', gap: 8,
};

/* ═══════════════════════════════════════════════════════════════════════════
   ChartInfoPanel (mirrors PnlDashboardCharts)
   ═══════════════════════════════════════════════════════════════════════════ */

interface InfoProps {
  title: string; description: string; interpretation: string[];
  example?: string; whyItMatters?: string; tips?: string[];
}

// SEQ-240: formulas moved to the "How is this worked out" pop-up beside each
// heading (useRetentionStats.metrics.ts). This panel keeps the reading guide.
function ChartInfoPanel({ title, description, interpretation, example, whyItMatters, tips }: InfoProps) {
  const [open, setOpen] = useState(false);
  const secTitle: React.CSSProperties = {
    fontSize: '0.76rem', fontWeight: 700, color: '#b45309',
    marginTop: 10, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5,
  };
  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%', justifyContent: 'center',
          background: open ? 'var(--bg-tertiary, #f9fafb)' : 'none',
          border: `1px solid ${open ? '#fbbf24' : 'var(--border-color, #e5e7eb)'}`,
          borderRadius: 8, padding: '6px 12px', cursor: 'pointer',
          fontSize: '0.75rem', color: open ? '#b45309' : 'var(--text-secondary, #6b7280)',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => { (e.currentTarget).style.borderColor = '#fbbf24'; (e.currentTarget).style.color = '#b45309'; }}
        onMouseLeave={(e) => { if (!open) { (e.currentTarget).style.borderColor = 'var(--border-color, #e5e7eb)'; (e.currentTarget).style.color = 'var(--text-secondary, #6b7280)'; } }}
      >
        <Info size={13} /> How to interpret this chart
        {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>
      {open && (
        <div style={{
          marginTop: 8, padding: '16px 18px',
          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
          borderRadius: 12, border: '1px solid #fde68a',
          fontSize: '0.8rem', lineHeight: 1.65, color: 'var(--text-primary, #374151)',
          animation: 'retFadeInUp 0.25s ease-out',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 6, color: '#b45309', fontSize: '0.85rem' }}>📖 {title}</div>
          <p style={{ margin: '0 0 6px', color: 'var(--text-secondary, #6b7280)', fontSize: '0.79rem' }}>{description}</p>
          {example && (<><div style={secTitle}>💡 Example</div><div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: 8, padding: '8px 12px', fontSize: '0.77rem', color: '#374151', border: '1px solid #fde68a', lineHeight: 1.6 }}>{example}</div></>)}
          <div style={secTitle}>📊 How to Read</div>
          <ul style={{ margin: '2px 0 0 16px', paddingLeft: 0, listStyle: 'disc', fontSize: '0.77rem' }}>
            {interpretation.map((item, i) => <li key={i} style={{ marginBottom: 3 }}>{item}</li>)}
          </ul>
          {whyItMatters && (<><div style={secTitle}>🎯 Why This Matters</div><p style={{ margin: 0, fontSize: '0.77rem', color: '#374151' }}>{whyItMatters}</p></>)}
          {tips && tips.length > 0 && (<><div style={secTitle}>💡 Pro Tips</div><ul style={{ margin: '2px 0 0 16px', paddingLeft: 0, listStyle: '▸ ', fontSize: '0.77rem' }}>{tips.map((t, i) => <li key={i} style={{ marginBottom: 3 }}>{t}</li>)}</ul></>)}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   1. Retention Overview Donut
   ═══════════════════════════════════════════════════════════════════════════ */

const DONUT_COLORS = ['#10b981', '#f59e0b'];

export function RetentionOverviewDonut({ totalHeld, totalReceived, totalOutstanding }: { totalHeld: number; totalReceived: number; totalOutstanding: number }) {
  const data = [
    { name: 'Received', value: totalReceived },
    { name: 'Outstanding', value: totalOutstanding },
  ].filter(d => d.value > 0);

  // If nothing received, show full outstanding
  if (data.length === 0) data.push({ name: 'Outstanding', value: totalOutstanding || 1 });

  return (
    <div className="ret-chart-card" style={cardStyle}>
      <div style={hdrStyle}>
        Retention Overview
        <HowWorkedOut definition={RETENTION_FIGURES["retention.overview"]} />
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={65} outerRadius={100} paddingAngle={2} dataKey="value" stroke="none">
            {data.map((_, i) => <Cell key={i} fill={data[i].name === 'Received' ? DONUT_COLORS[0] : DONUT_COLORS[1]} />)}
          </Pie>
          <Tooltip formatter={(v: number | undefined) => formatCurrency(v ?? 0)} contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '0.82rem' }} />
        </PieChart>
      </ResponsiveContainer>
      {/* Summary row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
        {[
          { label: 'Held', value: totalHeld, color: '#6b7280' },
          { label: 'Received', value: totalReceived, color: '#10b981' },
          { label: 'Outstanding', value: totalOutstanding, color: '#f59e0b' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center', padding: '8px 4px', background: `${s.color}08`, borderRadius: 10, border: `1px solid ${s.color}20` }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted, #9ca3af)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{s.label}</div>
            <div data-seq-private="" style={{ fontSize: '0.95rem', fontWeight: 700, color: s.color }}>{formatCurrency(s.value)}</div>
          </div>
        ))}
      </div>
      <ChartInfoPanel
        title="Retention Overview"
        description="Shows the split between retention money that has been received back vs. still held by the client."
        example="If $100K is held and $25K received, outstanding is $75K (75% still locked)."
        interpretation={[
          'Green = retention money received back (good)',
          'Amber = retention still held by the client (cash flow risk)',
          'Larger amber slice = more capital tied up',
        ]}
        whyItMatters="Outstanding retention is cash you've earned but can't use. Large outstanding amounts create cash flow pressure."
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   2. Retention By Project (Horizontal Bar)
   ═══════════════════════════════════════════════════════════════════════════ */

type RetentionLimitMode = 'top10' | 'bottom10' | 'all';

export function RetentionByProjectChart({ data }: { data: ProjectRetentionSummary[] }) {
  const navigate = useNavigate();
  // SEQ-245: same Top 10 / Bottom 10 / All selector as Profit % by project.
  // `data` arrives sorted by outstanding, largest first.
  const [limitMode, setLimitMode] = useState<RetentionLimitMode>('top10');
  const shown = limitMode === 'top10'
    ? data.slice(0, 10)
    : limitMode === 'bottom10'
      ? data.slice(-10).reverse()
      : data;
  const chartData = shown.map(p => ({
    name: p.projectName.length > 22 ? p.projectName.slice(0, 20) + '…' : p.projectName,
    fullName: p.projectName,
    received: p.totalReceived,
    outstanding: p.outstanding,
    projectId: p.projectId,
  }));

  return (
    <div className="ret-chart-card" style={cardStyle}>
      <div style={{ ...hdrStyle, justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Retention by Project
          <HowWorkedOut definition={RETENTION_FIGURES["retention.by-project"]} />
        </span>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>
          Show
          <select
            className="form-input ret-limit-select"
            value={limitMode}
            onChange={(e) => setLimitMode(e.target.value as RetentionLimitMode)}
            aria-label="Show projects in the Retention by Project chart"
            style={{ padding: '4px 36px 4px 8px', fontSize: 12, width: 'auto' }}
          >
            <option value="top10">Top 10</option>
            <option value="bottom10">Bottom 10</option>
            <option value="all">All projects</option>
          </select>
        </label>
      </div>
      <ResponsiveContainer width="100%" height={Math.max(300, shown.length * 42)}>
        <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" horizontal={false} />
          <XAxis type="number" tickFormatter={compactCurrencyTick} style={{ fontSize: '0.75rem' }} />
          <YAxis dataKey="name" type="category" width={140} style={{ fontSize: '0.72rem' }} tick={{ fill: 'var(--text-secondary, #6b7280)' }} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={((v: number | undefined, name: string) => [formatCurrency(v ?? 0), name === 'received' ? 'Received' : 'Outstanding']) as any}
            contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '0.82rem' }}
          />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          <Bar dataKey="received" stackId="a" fill="#10b981" name="Received" radius={[0, 0, 0, 0]}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            cursor="pointer" onClick={(d: any) => d?.projectId && navigate(`/projects/${d.projectId}`)} />
          <Bar dataKey="outstanding" stackId="a" fill="#f59e0b" name="Outstanding" radius={[0, 4, 4, 0]}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            cursor="pointer" onClick={(d: any) => d?.projectId && navigate(`/projects/${d.projectId}`)} />
        </BarChart>
      </ResponsiveContainer>
      <ChartInfoPanel
        title="Retention by Project"
        description="Shows how much retention each project has outstanding. Click a bar to navigate to the project."
        interpretation={[
          'Longer amber bars = more retention locked up on that project',
          'Green portion = retention already received back',
          'Projects sorted by highest outstanding first',
        ]}
        whyItMatters="Helps prioritize which projects to follow up on for retention release."
        tips={['Click any bar to view the project detail page', 'Use the Show menu for Top 10, Bottom 10 or all projects']}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   3. Monthly Trend Chart (Cumulative)
   ═══════════════════════════════════════════════════════════════════════════ */

export function RetentionMonthlyTrendChart({ data }: { data: MonthlyRetentionTrend[] }) {
  return (
    <div className="ret-chart-card" style={cardStyle}>
      <div style={hdrStyle}>
        Retention Trend (Cumulative)
        <HowWorkedOut definition={RETENTION_FIGURES["retention.trend"]} />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} margin={{ left: 10, right: 20, top: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="retHeldGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.02} />
            </linearGradient>
            <linearGradient id="retRecvGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
          <XAxis dataKey="label" style={{ fontSize: '0.72rem' }} tick={{ fill: 'var(--text-secondary, #6b7280)' }} angle={-35} textAnchor="end" height={60} />
          <YAxis tickFormatter={compactCurrencyTick} style={{ fontSize: '0.72rem' }} tick={{ fill: 'var(--text-secondary, #6b7280)' }} />
          {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
          <Tooltip formatter={((v: number | undefined, name: string) => [formatCurrency(v ?? 0), name]) as any} contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '0.82rem' }} />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          <Area type="monotone" dataKey="cumulativeHeld" name="Cumulative Held" fill="url(#retHeldGrad)" stroke="#f59e0b" strokeWidth={2} dot={false} />
          <Area type="monotone" dataKey="cumulativeReceived" name="Cumulative Received" fill="url(#retRecvGrad)" stroke="#10b981" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="cumulativeOutstanding" name="Outstanding" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartInfoPanel
        title="Retention Trend"
        description="Tracks how cumulative retention held and received change month-over-month."
        interpretation={[
          'Amber area = total retention held over time (grows with new claims)',
          'Green area = total retention received back over time',
          'Red dashed line = gap (outstanding retention)',
          'If the gap widens, retention is accumulating faster than being recovered',
        ]}
        whyItMatters="A widening gap between held and received means increasing cash flow exposure."
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   4. Retention Aging Chart
   ═══════════════════════════════════════════════════════════════════════════ */

export function RetentionAgingChart({ data }: { data: RetentionAgingBucket[] }) {
  return (
    <div className="ret-chart-card" style={cardStyle}>
      <div style={hdrStyle}>
        Retention Aging Analysis
        <HowWorkedOut definition={RETENTION_FIGURES["retention.aging"]} />
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ left: 10, right: 20, top: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
          <XAxis dataKey="label" style={{ fontSize: '0.75rem' }} tick={{ fill: 'var(--text-secondary, #6b7280)' }} />
          <YAxis tickFormatter={compactCurrencyTick} style={{ fontSize: '0.72rem' }} tick={{ fill: 'var(--text-secondary, #6b7280)' }} />
          <Tooltip
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={((v: number | undefined, _: string, props: { payload?: { count?: number } }) => [formatCurrency(v ?? 0), `${props.payload?.count || 0} claims`]) as any}
            contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '0.82rem' }}
          />
          <Bar dataKey="amount" name="Outstanding" radius={[6, 6, 0, 0]}>
            {data.map((entry, i) => <Cell key={i} fill={entry.color} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <ChartInfoPanel
        title="Retention Aging"
        description="Groups outstanding retention by how long it's been held, highlighting overdue amounts."
        interpretation={[
          'Green (0-3 months) = recent retention, normal holding period',
          'Amber (3-6 months) = moderate age, should be tracked',
          'Orange (6-12 months) = getting old, consider follow-up',
          'Red (12+ months) = overdue, requires immediate action',
        ]}
        whyItMatters="Older retention is harder to recover. This chart highlights where follow-up is needed most."
        tips={['Focus collection efforts on the red and orange buckets first']}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   5. Recovery Rate Gauge
   ═══════════════════════════════════════════════════════════════════════════ */

export function RetentionRecoveryGauge({ recoveryPercent }: { recoveryPercent: number }) {
  const gaugeColor = recoveryPercent >= 70 ? '#10b981' : recoveryPercent >= 30 ? '#f59e0b' : '#ef4444';
  const data = [{ name: 'Recovery', value: Math.min(recoveryPercent, 100), fill: gaugeColor }];

  return (
    <div className="ret-chart-card" style={cardStyle}>
      <div style={hdrStyle}>
        Recovery Rate
        <HowWorkedOut definition={RETENTION_FIGURES["retention.recovery-gauge"]} />
      </div>
      <div style={{ position: 'relative' }}>
        <ResponsiveContainer width="100%" height={260}>
          <RadialBarChart cx="50%" cy="50%" innerRadius="60%" outerRadius="90%" startAngle={180} endAngle={0} data={data} barSize={18}>
            <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
            <RadialBar background={{ fill: 'var(--bg-tertiary, #f3f4f6)' }} dataKey="value" angleAxisId={0} cornerRadius={10} />
          </RadialBarChart>
        </ResponsiveContainer>
        {/* Center label */}
        <div style={{ position: 'absolute', top: '42%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 800, color: gaugeColor, lineHeight: 1 }}>{recoveryPercent.toFixed(1)}%</div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #9ca3af)', marginTop: 4 }}>Recovered</div>
        </div>
      </div>
      <ChartInfoPanel
        title="Recovery Rate"
        description="Shows what percentage of total retention held has been received back."
        interpretation={[
          'Green (70%+) = healthy recovery',
          'Amber (30-70%) = moderate, needs monitoring',
          'Red (<30%) = poor recovery, cash flow at risk',
        ]}
        whyItMatters="Low recovery rates mean capital is locked up, reducing available working cash."
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   6. Per-Project Trend (select a project, see its monthly retention)
   ═══════════════════════════════════════════════════════════════════════════ */

export function ProjectRetentionTrendChart({ data }: { data: ProjectMonthlyRetention[] }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const project = data[selectedIdx];
  if (!project) return null;

  return (
    <div className="ret-chart-card" style={cardStyle}>
      <div style={hdrStyle}>
        Per-Project Retention Trend
        <HowWorkedOut definition={RETENTION_FIGURES["retention.project-trend"]} />
      </div>
      {/* Project selector */}
      <div style={{ marginBottom: 12 }}>
        <select
          value={selectedIdx}
          onChange={(e) => setSelectedIdx(Number(e.target.value))}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 10,
            border: '1px solid var(--border-color, #e5e7eb)',
            background: 'var(--bg-secondary, #fff)',
            fontSize: '0.82rem', color: 'var(--text-primary, #111827)',
            cursor: 'pointer',
          }}
        >
          {data.map((p, i) => (
            <option key={p.projectId} value={i}>
              {p.projectName}: {formatCurrency(p.totalHeld)} held
            </option>
          ))}
        </select>
      </div>
      {/* Summary chips */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[
          { label: 'Held', value: project.totalHeld, color: '#f59e0b' },
          { label: 'Received', value: project.totalReceived, color: '#10b981' },
          { label: 'Outstanding', value: project.totalHeld - project.totalReceived, color: '#ef4444' },
        ].map(c => (
          <div key={c.label} style={{ padding: '4px 12px', borderRadius: 20, background: `${c.color}10`, border: `1px solid ${c.color}30`, fontSize: '0.75rem', fontWeight: 600, color: c.color }}>
            {c.label}: {formatCurrency(c.value)}
          </div>
        ))}
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <ComposedChart data={project.monthlyData} margin={{ left: 10, right: 20, top: 10, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #e5e7eb)" />
          <XAxis dataKey="label" style={{ fontSize: '0.72rem' }} tick={{ fill: 'var(--text-secondary, #6b7280)' }} angle={-35} textAnchor="end" height={55} />
          <YAxis tickFormatter={compactCurrencyTick} style={{ fontSize: '0.72rem' }} tick={{ fill: 'var(--text-secondary, #6b7280)' }} />
          <Tooltip formatter={(v: number | undefined) => formatCurrency(v ?? 0)} contentStyle={{ borderRadius: 10, border: '1px solid #e5e7eb', fontSize: '0.82rem' }} />
          <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
          <Bar dataKey="held" name="Monthly Held" fill="#f59e0b" radius={[4, 4, 0, 0]} opacity={0.7} />
          <Bar dataKey="received" name="Monthly Received" fill="#10b981" radius={[4, 4, 0, 0]} opacity={0.7} />
          <Line type="monotone" dataKey="cumulativeHeld" name="Cumulative Held" stroke="#d97706" strokeWidth={2} dot={{ r: 3, fill: '#d97706' }} />
          <Line type="monotone" dataKey="cumulativeReceived" name="Cumulative Received" stroke="#059669" strokeWidth={2} dot={{ r: 3, fill: '#059669' }} />
        </ComposedChart>
      </ResponsiveContainer>
      <ChartInfoPanel
        title="Per-Project Retention Trend"
        description="Monthly retention breakdown for a selected project: bars show each month's retention, lines show cumulative totals."
        interpretation={[
          'Amber bars = retention held each month for this project',
          'Green bars = retention received each month',
          'Lines track cumulative totals over time',
          'Use the dropdown to switch between projects',
        ]}
        whyItMatters="Lets you drill into individual project retention patterns to identify which projects have the most locked-up cash."
      />
    </div>
  );
}
